import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Stack,
  Typography,
  Chip,
  Button,
  TextField,
  Tooltip,
  IconButton,
  Collapse,
  Fade,
  alpha,
  useTheme,
} from "@mui/material";
import { TransitionGroup } from "react-transition-group";
import ArticleIcon from "@mui/icons-material/Article";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const SYSTEM_TAG_TOOLTIP =
  "System tags are managed automatically and cannot be edited or deleted.";

const DisclosureInspector = ({
  selectedDisclosure,
  openEdit,
  openDelete,
  disclosureTagPanelSharedProps,
}) => {
  const theme = useTheme();
  const [tagManagementOpen, setTagManagementOpen] = useState(false);
  const [flashTagId, setFlashTagId] = useState(null);

  const {
    isMdUp,
    tags = [],
    tagSearch,
    setTagSearch,
    tagNameInput,
    setTagNameInput,
    createTagInLibrary,
    saveTagAssignments,
    tagsSubmitting,
    tagsLoading,
    editingTagId,
    editingTagName,
    setEditingTagName,
    handleSaveEditTag,
    setEditingTagId,
    handleStartEditTag,
    handleDeleteTag,
    setTagsDrawerOpen,
  } = disclosureTagPanelSharedProps || {};

  const [draftTagIds, setDraftTagIds] = useState(() => new Set());
  const [tagDraftDirty, setTagDraftDirty] = useState(false);

  useEffect(() => {
    const ids = new Set(
      (selectedDisclosure?.disclosure_tags || [])
        .map((t) => String(t.t_id))
        .filter(Boolean)
    );
    setDraftTagIds(ids);
    setTagDraftDirty(false);
  }, [selectedDisclosure?.d_id]);

  useEffect(() => {
    if (tagDraftDirty) return;
    if (!selectedDisclosure?.d_id) return;
    const ids = new Set(
      (selectedDisclosure?.disclosure_tags || [])
        .map((t) => String(t.t_id))
        .filter(Boolean)
    );
    setDraftTagIds(ids);
  }, [
    selectedDisclosure?.disclosure_tags,
    selectedDisclosure?.d_id,
    tagDraftDirty,
  ]);

  const serverTagIdSet = useMemo(
    () =>
      new Set(
        (selectedDisclosure?.disclosure_tags || [])
          .map((t) => String(t.t_id))
          .filter(Boolean)
      ),
    [selectedDisclosure?.disclosure_tags, selectedDisclosure?.d_id]
  );

  const toAddIds = useMemo(
    () => [...draftTagIds].filter((id) => !serverTagIdSet.has(id)),
    [draftTagIds, serverTagIdSet]
  );

  const toRemoveIds = useMemo(
    () => [...serverTagIdSet].filter((id) => !draftTagIds.has(id)),
    [draftTagIds, serverTagIdSet]
  );

  const tagAssignmentDirty = toAddIds.length > 0 || toRemoveIds.length > 0;

  const tagById = useMemo(() => {
    const m = new Map();
    (tags || []).forEach((t) => m.set(String(t.t_id), t));
    (selectedDisclosure?.disclosure_tags || []).forEach((t) =>
      m.set(String(t.t_id), t)
    );
    return m;
  }, [tags, selectedDisclosure?.disclosure_tags]);

  const draftTagsOrdered = useMemo(() => {
    return [...draftTagIds]
      .map((id) => tagById.get(id))
      .filter(Boolean)
      .sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        })
      );
  }, [draftTagIds, tagById]);

  const handleDraftToggleTag = useCallback((tag) => {
    setFlashTagId(String(tag.t_id));
    window.setTimeout(() => setFlashTagId(null), 450);
    setDraftTagIds((prev) => {
      const next = new Set(prev);
      const id = String(tag.t_id);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setTagDraftDirty(true);
  }, []);

  const handleCreateTagDraft = async () => {
    if (!tagNameInput?.trim() || tagsSubmitting || !createTagInLibrary) return;
    const created = await createTagInLibrary();
    const payload = created?.data ?? created;
    const newId = payload?.t_id;
    if (!newId) return;
    setDraftTagIds((prev) => new Set([...prev, String(newId)]));
    setTagDraftDirty(true);
  };

  const handleSaveTagDraft = async () => {
    if (!saveTagAssignments || tagsSubmitting) return;
    const ok = await saveTagAssignments([...draftTagIds]);
    if (ok) setTagDraftDirty(false);
  };

  const handleDiscardTagDraft = () => {
    const ids = new Set(
      (selectedDisclosure?.disclosure_tags || [])
        .map((t) => String(t.t_id))
        .filter(Boolean)
    );
    setDraftTagIds(ids);
    setTagDraftDirty(false);
  };

  const searchTrimmed = (tagSearch || "").trim();
  const searchLower = searchTrimmed.toLowerCase();

  const tagNameMatchesSearch = (tag) =>
    !searchLower || (tag.name || "").toLowerCase().includes(searchLower);

  const displayTags = useMemo(() => {
    const list = [...tags];
    const match = (t) =>
      !searchLower || (t.name || "").toLowerCase().includes(searchLower);
    if (!searchLower) {
      return list.sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        })
      );
    }
    return list.sort((a, b) => {
      const ma = match(a);
      const mb = match(b);
      if (ma && !mb) return -1;
      if (!ma && mb) return 1;
      return (a.name || "").localeCompare(b.name || "", undefined, {
        sensitivity: "base",
      });
    });
  }, [tags, searchLower]);

  const searchHasNoMatches =
    Boolean(searchLower) &&
    tags.length > 0 &&
    !tags.some((t) => (t.name || "").toLowerCase().includes(searchLower));

  useEffect(() => {
    setTagManagementOpen(false);
    setEditingTagId(null);
    setEditingTagName("");
  }, [selectedDisclosure?.d_id, setEditingTagId, setEditingTagName]);

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        bgcolor: "#FAFAFA",
        borderLeft: "1px solid",
        borderColor: "white",
        boxShadow: (t) => `-12px 0 40px ${alpha(t.palette.common.black, 0.06)}`,
      }}
    >
      {!selectedDisclosure ? (
        <Stack
          direction='column'
          alignItems='center'
          justifyContent='center'
          spacing={2}
          p={4}
          textAlign='center'
          flex={1}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: "grey.100",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
              color: "grey.400",
            }}
          >
            <ArticleIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box>
            <Typography
              variant='h6'
              color='text.primary'
              fontWeight={600}
              sx={{ mb: 0.5 }}
            >
              No Disclosure Selected
            </Typography>
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ maxWidth: 320, mx: "auto" }}
            >
              Select an item from the list to view its full statement and manage
              associated tags.
            </Typography>
          </Box>
        </Stack>
      ) : (
        <>
          <Box
            sx={{
              px: { xs: 2.5, md: 4 },
              py: 2.5,
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
              zIndex: 1,
            }}
          >
            <Stack direction='column' spacing={2}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant='h5'
                  fontWeight={800}
                  color='text.primary'
                  sx={{
                    mb: 0.5,
                    lineHeight: 1.2,
                    wordBreak: "break-word",
                    letterSpacing: "-0.015em",
                  }}
                >
                  {selectedDisclosure.title}
                </Typography>
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems='center'
                justifyContent='space-between'
                spacing={1.5}
              >
                <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                  <Chip
                    size='small'
                    label={selectedDisclosure.is_active ? "Active" : "Inactive"}
                    color={selectedDisclosure.is_active ? "success" : "default"}
                    variant={
                      selectedDisclosure.is_active ? "filled" : "outlined"
                    }
                    sx={{ fontWeight: 600, px: 0.5 }}
                  />
                  {selectedDisclosure.company?.name ? (
                    <Chip
                      size='small'
                      variant='outlined'
                      label={selectedDisclosure.company.name}
                      sx={{
                        borderColor: "divider",
                        color: "text.secondary",
                        fontWeight: 500,
                      }}
                    />
                  ) : null}
                </Stack>

                <Stack direction='row' spacing={1}>
                  <Button
                    variant='outlined'
                    size='small'
                    startIcon={<EditIcon fontSize='small' />}
                    onClick={() => openEdit(selectedDisclosure)}
                    sx={{
                      borderRadius: 1.5,
                      textTransform: "none",
                      fontWeight: 600,
                      px: 2,
                      borderColor: "divider",
                      color: "text.secondary",
                      "&:hover": {
                        borderColor: "primary.main",
                        color: "primary.main",
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.04
                        ),
                      },
                    }}
                  >
                    Edit
                  </Button>

                  <Button
                    variant='outlined'
                    size='small'
                    startIcon={<DeleteOutlineIcon fontSize='small' />}
                    onClick={() => openDelete(selectedDisclosure)}
                    color='error'
                    sx={{
                      borderRadius: 1.5,
                      textTransform: "none",
                      fontWeight: 600,
                      px: 2,
                      borderColor: "error.main",
                      color: "error.main",
                      "& .MuiButton-startIcon": {
                        color: "error.main",
                      },
                      "&:hover": {
                        borderColor: "error.main",
                        color: "error.main",
                        backgroundColor: alpha(theme.palette.error.main, 0.04),
                      },
                    }}
                  >
                    Delete
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </Box>

          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              overflow: "hidden",
              bgcolor: "#FFFFFF",
            }}
          >
            <Box
              sx={{
                px: { xs: 2.5, md: 4 },
                py: 3,
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                borderBottom: "1px solid",
                borderColor: "divider",
                "&::-webkit-scrollbar": { width: 6 },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: "grey.200",
                  borderRadius: 3,
                },
              }}
            >
              <Typography
                variant='overline'
                color='primary'
                fontWeight={700}
                display='block'
                sx={{
                  mb: 1.5,
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                }}
              >
                Statement Content
              </Typography>

              <Typography
                variant='body1'
                color='text.primary'
                sx={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  lineHeight: 1.8,
                  fontSize: "0.95rem",
                  color: "grey.700",
                }}
              >
                {selectedDisclosure.statement_text || (
                  <Typography
                    variant='body2'
                    color='text.disabled'
                    sx={{ fontStyle: "italic" }}
                  >
                    No statement text available.
                  </Typography>
                )}
              </Typography>
            </Box>

            <Box
              sx={{
                p: 3,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: tagManagementOpen
                  ? theme.palette.background.paper
                  : alpha(theme.palette.grey[500], 0.06),
                boxShadow: tagManagementOpen
                  ? `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`
                  : `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.8)}`,
                transition:
                  "background-color 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
                overflow: "hidden",
              }}
            >
              <Stack
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                spacing={1}
                sx={{ mb: 1 }}
              >
                <Stack direction='row' alignItems='center' spacing={1}>
                  <LocalOfferIcon color='primary' fontSize='small' />
                  <Typography variant='subtitle2' fontWeight={800}>
                    Tag workbench
                  </Typography>
                </Stack>
                <Button
                  size='small'
                  variant={tagManagementOpen ? "outlined" : "text"}
                  color='primary'
                  onClick={() => setTagManagementOpen((o) => !o)}
                  endIcon={
                    tagManagementOpen ? (
                      <ExpandLessIcon fontSize='small' />
                    ) : (
                      <ExpandMoreIcon fontSize='small' />
                    )
                  }
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {tagManagementOpen ? "Hide tag editor" : "Edit tags"}
                </Button>
              </Stack>

              <Collapse in={!tagManagementOpen} timeout={280}>
                <Box sx={{ pt: 0.5 }}>
                  {selectedDisclosure.disclosure_tags?.length ? (
                    <Stack
                      direction='row'
                      flexWrap='wrap'
                      useFlexGap
                      spacing={0.75}
                      sx={{ gap: 0.75 }}
                    >
                      {selectedDisclosure.disclosure_tags.map((t) => (
                        <Chip
                          key={t.t_id}
                          label={t.name}
                          size='small'
                          variant='outlined'
                          color='primary'
                          sx={{
                            bgcolor: theme.palette.primary.main,
                            color: theme.palette.common.white,
                            fontWeight: 600,
                            borderColor: theme.palette.primary.main,
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary.main, 0.2),
                              color: theme.palette.primary.main,
                              borderColor: theme.palette.primary.main,
                            },
                          }}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      No tags on this disclosure yet. Open the editor to add
                      some.
                    </Typography>
                  )}
                </Box>
              </Collapse>

              <Collapse in={tagManagementOpen} timeout={320}>
                <Box
                  sx={{
                    pt: 2,
                    height: "min(48vh, 480px)",
                    minHeight: 260,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems='stretch'
                    sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}
                  >
                    <Box
                      sx={{
                        flex: { xs: "1 1 auto", md: "1 1 58%" },
                        minWidth: 0,
                        minHeight: 0,
                        maxHeight: { xs: "min(36vh, 220px)", md: "none" },
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        overflow: "hidden",
                      }}
                    >
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        fontWeight={700}
                        sx={{ flexShrink: 0 }}
                      >
                        Available tags
                      </Typography>
                      <TextField
                        label='Search tags'
                        value={tagSearch}
                        onChange={(e) => setTagSearch(e.target.value)}
                        size='small'
                        fullWidth
                        sx={{ flexShrink: 0 }}
                      />
                      <Stack
                        direction='row'
                        spacing={1}
                        alignItems='flex-start'
                        sx={{ flexShrink: 0 }}
                      >
                        <TextField
                          label='New tag'
                          value={tagNameInput}
                          onChange={(e) => setTagNameInput(e.target.value)}
                          size='small'
                          fullWidth
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleCreateTagDraft();
                            }
                          }}
                        />
                        <Button
                          variant='contained'
                          onClick={handleCreateTagDraft}
                          disabled={!tagNameInput?.trim() || tagsSubmitting}
                          sx={{
                            flexShrink: 0,
                            mt: 0.5,
                            boxShadow:
                              tags.length === 0
                                ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.35)}`
                                : undefined,
                          }}
                        >
                          Add
                        </Button>
                      </Stack>
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          overflowY: "auto",
                          overflowX: "hidden",
                          pr: 0.5,
                        }}
                      >
                        {tagsLoading ? (
                          <Typography variant='body2' color='text.secondary'>
                            Loading tags…
                          </Typography>
                        ) : searchHasNoMatches ? (
                          <Typography variant='body2' color='text.secondary'>
                            No matching tags — try different keywords.
                          </Typography>
                        ) : tags.length === 0 ? (
                          <Typography variant='body2' color='text.secondary'>
                            No tags in the library yet. Create your first tag
                            using the field above.
                          </Typography>
                        ) : (
                          <Stack spacing={1}>
                            {displayTags.map((tag) => {
                              const assigned = draftTagIds.has(
                                String(tag.t_id)
                              );
                              const isEditing = editingTagId === tag.t_id;
                              const isSystem = Boolean(tag.is_system);
                              const matches = tagNameMatchesSearch(tag);
                              const dimmed = Boolean(searchLower) && !matches;
                              const isFlashing =
                                flashTagId === String(tag.t_id);
                              return (
                                <Box
                                  key={tag.t_id}
                                  sx={{
                                    border: "1px solid",
                                    borderColor: assigned
                                      ? "primary.main"
                                      : "divider",
                                    borderRadius: 1.5,
                                    px: 1,
                                    py: 0.75,
                                    opacity: dimmed ? 0.38 : 1,
                                    pointerEvents: dimmed ? "none" : "auto",
                                    bgcolor: assigned
                                      ? alpha(theme.palette.primary.main, 0.07)
                                      : "background.paper",
                                    boxShadow: isFlashing
                                      ? `0 0 0 2px ${theme.palette.primary.main}`
                                      : "none",
                                    transition:
                                      "border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease",
                                  }}
                                >
                                  <Stack
                                    direction='row'
                                    spacing={1}
                                    alignItems='center'
                                    justifyContent='space-between'
                                  >
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                      {isEditing ? (
                                        <TextField
                                          size='small'
                                          value={editingTagName}
                                          onChange={(e) =>
                                            setEditingTagName(e.target.value)
                                          }
                                          fullWidth
                                        />
                                      ) : (
                                        <Stack
                                          direction='row'
                                          spacing={0.75}
                                          alignItems='center'
                                          flexWrap='wrap'
                                        >
                                          {assigned ? (
                                            <CheckCircleIcon
                                              color='primary'
                                              sx={{
                                                fontSize: 18,
                                                flexShrink: 0,
                                              }}
                                            />
                                          ) : null}
                                          <Chip
                                            size='small'
                                            label={tag.name}
                                            color={
                                              assigned ? "primary" : "default"
                                            }
                                            variant={
                                              assigned ? "filled" : "outlined"
                                            }
                                            onClick={
                                              dimmed
                                                ? undefined
                                                : () =>
                                                    handleDraftToggleTag(tag)
                                            }
                                            clickable={!dimmed}
                                          />
                                          {isSystem ? (
                                            <Tooltip
                                              title={SYSTEM_TAG_TOOLTIP}
                                              placement='top'
                                              arrow
                                            >
                                              <Chip
                                                size='small'
                                                label='System'
                                                color='secondary'
                                                variant='outlined'
                                              />
                                            </Tooltip>
                                          ) : null}
                                        </Stack>
                                      )}
                                    </Box>

                                    <Stack direction='row' spacing={0.25}>
                                      {isEditing ? (
                                        <>
                                          <Tooltip title='Save'>
                                            <span>
                                              <IconButton
                                                size='small'
                                                color='primary'
                                                onClick={handleSaveEditTag}
                                                disabled={
                                                  !editingTagName?.trim() ||
                                                  tagsSubmitting
                                                }
                                              >
                                                <CheckIcon fontSize='small' />
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                          <Tooltip title='Cancel'>
                                            <IconButton
                                              size='small'
                                              onClick={() => {
                                                setEditingTagId(null);
                                                setEditingTagName("");
                                              }}
                                            >
                                              <CloseIcon fontSize='small' />
                                            </IconButton>
                                          </Tooltip>
                                        </>
                                      ) : (
                                        <>
                                          <Tooltip
                                            title={
                                              isSystem
                                                ? SYSTEM_TAG_TOOLTIP
                                                : "Edit tag"
                                            }
                                          >
                                            <span>
                                              <IconButton
                                                size='small'
                                                onClick={() =>
                                                  handleStartEditTag(tag)
                                                }
                                                disabled={
                                                  isSystem || tagsSubmitting
                                                }
                                              >
                                                <EditIcon fontSize='small' />
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                          <Tooltip
                                            title={
                                              isSystem
                                                ? SYSTEM_TAG_TOOLTIP
                                                : "Delete tag"
                                            }
                                          >
                                            <span>
                                              <IconButton
                                                size='small'
                                                color='error'
                                                onClick={() =>
                                                  handleDeleteTag(tag)
                                                }
                                                disabled={
                                                  isSystem || tagsSubmitting
                                                }
                                              >
                                                <DeleteOutlineIcon fontSize='small' />
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                        </>
                                      )}
                                    </Stack>
                                  </Stack>
                                </Box>
                              );
                            })}
                          </Stack>
                        )}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        flex: { xs: "0 0 auto", md: "1 1 38%" },
                        minWidth: 0,
                        minHeight: 0,
                        borderLeft: { md: "1px solid" },
                        borderTop: { xs: "1px solid", md: "none" },
                        borderColor: "divider",
                        pt: { xs: 2, md: 0 },
                        pl: { md: 2 },
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                        overflow: "hidden",
                      }}
                    >
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        fontWeight={700}
                        sx={{ flexShrink: 0 }}
                      >
                        On this disclosure
                      </Typography>

                      {tagAssignmentDirty ? (
                        <Alert
                          severity='info'
                          variant='outlined'
                          sx={{
                            py: 0.75,
                            fontSize: "0.8125rem",
                            flexShrink: 0,
                          }}
                        >
                          Unsaved: <strong>{toAddIds.length}</strong> to add,{" "}
                          <strong>{toRemoveIds.length}</strong> to remove on
                          this disclosure. Save to apply or discard to revert.
                        </Alert>
                      ) : null}

                      <Stack direction='row' spacing={1} sx={{ flexShrink: 0 }}>
                        <Button
                          variant='contained'
                          color='primary'
                          size='small'
                          disabled={!tagAssignmentDirty || tagsSubmitting}
                          onClick={handleSaveTagDraft}
                        >
                          Save tags
                        </Button>
                        <Button
                          variant='outlined'
                          size='small'
                          disabled={!tagAssignmentDirty || tagsSubmitting}
                          onClick={handleDiscardTagDraft}
                        >
                          Discard
                        </Button>
                      </Stack>

                      <Box
                        sx={{
                          flex: { xs: "0 0 auto", md: "1 1 0" },
                          minHeight: 0,
                          maxHeight: { xs: 120, md: "none" },
                          overflowY: "auto",
                          overflowX: "hidden",
                        }}
                      >
                        {draftTagsOrdered.length ? (
                          <TransitionGroup
                            component={Box}
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.75,
                            }}
                          >
                            {draftTagsOrdered.map((t) => (
                              <Fade key={String(t.t_id)} timeout={220} appear>
                                <Box
                                  component='span'
                                  sx={{
                                    display: "inline-block",
                                    transition:
                                      "opacity 0.2s ease, transform 0.2s ease",
                                  }}
                                >
                                  <Chip
                                    label={t.name}
                                    size='medium'
                                    color='primary'
                                    variant={
                                      t.is_system ? "outlined" : "filled"
                                    }
                                    onDelete={() => handleDraftToggleTag(t)}
                                    sx={{ fontWeight: 600 }}
                                  />
                                </Box>
                              </Fade>
                            ))}
                          </TransitionGroup>
                        ) : (
                          <Typography variant='body2' color='text.secondary'>
                            None assigned — pick tags from the left.
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Stack>
                </Box>
              </Collapse>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default DisclosureInspector;
